<template>
  <ListingsContainer
    v-if="!shouldHideContainer"
    :listings="listings"
    :horizontal-scrollable="horizontalScrollable"
    :responsive="responsive"
    :max-responsive-columns="maxResponsiveColumns"
    :disable-infinite-scroll="disableInfiniteScroll || hideSeeMore"
    :fetching="loading"
    :title="title"
    :hide-horizontal-scrollable-buttons="hideHorizontalScrollableButtons"
    :show-vehicle-count="enableVehicleCount"
    :vehicle-count-data="computedVehicleCountData"
    :show-view-all="enableViewAll"
    @fetch-listings="getVehicles()"
    @view-all-click="handleViewAllClick"
  >
    <template v-slot:title-append>
      <slot name="title-append" />
    </template>
    <template v-slot:title-below>
      <slot name="title-below" />
    </template>
  </ListingsContainer>
</template>

<script>
import ListingsContainer from "@/components/VehicleListings/ListingsContainer.vue";
import vehicleListingsMixin from "@/mixins/vehicleListings";

export default {
  name: "VehicleListingsBase",
  mixins: [vehicleListingsMixin],
  components: { ListingsContainer },
  emits: [
    "loaded-totals",
    "loaded-facets",
    "loaded-vehicles",
    "view-all-click"
  ],
  data() {
    return {
      hasLoadedOnce: false
    };
  },
  props: {
    // Display control props
    enableVehicleCount: {
      type: Boolean,
      default: false
      // When true, shows vehicle count badge AND tracks count changes over time
    },
    enableViewAll: {
      type: Boolean,
      default: false
      // When true, shows "View All" button AND enables navigation to search page
      // with filters/scoring/sorting from this component
    },
    trackingId: {
      type: String,
      default: null
      // Unique ID for tracking this section's count history
      // If not provided, uses component name or generates random ID
    },
    hideOnEmpty: {
      type: Boolean,
      default: false
      // When true, hides the entire ListingsContainer when listings.length === 0
    }
    // All other props (title, filters, scoring, sorting, options, etc.)
    // are inherited from the vehicleListings mixin
  },
  computed: {
    effectiveTrackingId() {
      return (
        this.trackingId ||
        this.$options.name ||
        "listing_" +
          Math.random()
            .toString(36)
            .substr(2, 9)
      );
    },
    computedVehicleCountData() {
      if (!this.enableVehicleCount || !this.vehicleTotalsData) {
        return null;
      }
      return this.calculateVehicleCountData(this.vehicleTotalsData);
    },
    shouldHideContainer() {
      // Only hide if hideOnEmpty is true AND we've loaded once AND there are no listings
      return (
        this.hideOnEmpty && this.hasLoadedOnce && this.listings.length === 0
      );
    }
  },
  watch: {
    listings() {
      // Mark as loaded once we have received listings (even if empty)
      if (!this.hasLoadedOnce && !this.loading) {
        this.hasLoadedOnce = true;
      }
    },
    loading(isLoading) {
      // When loading completes, mark as loaded
      if (!isLoading && !this.hasLoadedOnce) {
        this.hasLoadedOnce = true;
      }
    }
  },
  mounted() {
    // Only auto-fetch if not overridden by a child component
    // Child components that need custom setup should set skipVehicleListingsAutoFetch: true
    if (!this.$options.skipVehicleListingsAutoFetch) {
      this.getVehicles();
    }
  },
  methods: {
    calculateVehicleCountData(totalsData) {
      let showCountSinceLastVisit = false;
      let total = totalsData.results;

      // Session-based tracking using trackingId
      const SESSION_KEY =
        "carscience-vehicle-section-last-count-" +
        this.effectiveTrackingId +
        "-" +
        (this.$store.state.search.location.postalCode || "") +
        "-" +
        this.$store.state.search.location.radius;
      const currentSessionId = sessionStorage.getItem(SESSION_KEY);

      if (!currentSessionId) {
        // New session - generate session ID
        const newSessionId = Date.now().toString();
        sessionStorage.setItem(SESSION_KEY, newSessionId);

        if (localStorage.getItem(SESSION_KEY)) {
          showCountSinceLastVisit = true;
        }
      }

      // Get the baseline count from localStorage (from previous session)
      let previousCount = localStorage.getItem(SESSION_KEY);
      previousCount = previousCount ? parseInt(previousCount) : 0;

      // Store current count as baseline for next session
      localStorage.setItem(SESSION_KEY, total);

      let percentChange = null;

      // Calculate percent change if we have a previous count
      if (previousCount !== null && previousCount > 0) {
        let change = ((total - previousCount) / previousCount) * 100;
        percentChange = Math.round(change * 10) / 10; // Round to 1 decimal
      }

      return {
        total: total,
        percentChange: percentChange,
        showCountSinceLastVisit: showCountSinceLastVisit
      };
    },
    handleViewAllClick() {
      if (this.enableViewAll) {
        // Use innerFilters/innerScoring/innerSorting if available, otherwise use props
        let searchFilters =
          this.innerFilters && this.innerFilters.length > 0
            ? this.innerFilters
            : this.filters;
        let searchScoring =
          this.innerScoring && this.innerScoring.length > 0
            ? this.innerScoring
            : this.scoring;
        let searchSorting =
          this.innerSorting && this.innerSorting.length > 0
            ? this.innerSorting
            : this.sorting;

        this.$store.commit("search/SET_SEARCH_FILTERS", searchFilters);
        this.$store.commit("search/SET_SEARCH_SORTING", searchSorting);
        if (searchScoring.length > 0) {
          this.$store.commit("search/SET_SEARCH_SCORING", searchScoring);
        }

        this.$store.commit("search/RESET_PAGINATION");
        this.$store.commit("search/SET_LOADING");
        if (this.$route.name !== "search") {
          this.$router.push({ name: "search" });
        }
      }

      // Emit for parent to listen to as well
      this.$emit("view-all-click");
    }
  }
};
</script>
